import { z } from 'zod';
import { pageQuerySchema } from './marketplace.js';
import { userRoleSchema } from './schemas.js';
/**
 * Reviews and reputation. [Parent step 21, Job Poster steps 14/29]
 *
 * A review is earned, not offered: only the two people on an approved
 * assignment may write one, and only about each other. That is what stops the
 * rating from becoming something either side can farm, and it is why the
 * summary below is safe to show next to an applicant's name.
 */
export const ratingSchema = z
    .number()
    .int()
    .min(1, 'Pick a rating from 1 to 5')
    .max(5, 'Pick a rating from 1 to 5');
export const reviewSchema = z.object({
    id: z.string(),
    /** The assignment this review is about — every review has one. */
    assignmentId: z.string(),
    jobTitle: z.string(),
    rating: ratingSchema,
    comment: z.string().nullable(),
    /** Who wrote it. */
    authorName: z.string(),
    authorRole: userRoleSchema,
    createdAt: z.string(),
    /** True when the signed-in account wrote this one. */
    isMine: z.boolean(),
});
export const createReviewInputSchema = z.object({
    assignmentId: z.string(),
    rating: ratingSchema,
    comment: z.string().trim().max(2000).optional(),
});
export const reviewListQuerySchema = pageQuerySchema.extend({
    /**
     * Whose reputation to read, as any publicId that identifies them — a teen,
     * parent or job poster profile id, or the account id.
     */
    personId: z.string().uuid().optional(),
});
/**
 * The compact reputation shown beside a name.
 *
 * `count` matters as much as `average`: a single five-star review is not the
 * same claim as forty, and a screen that hides the count invites the reader to
 * treat them alike.
 */
export const reputationSchema = z.object({
    average: z.number().min(0).max(5).nullable(),
    count: z.number().int().min(0),
    /** How many of each star, newest reputation first: index 0 is one star. */
    distribution: z.array(z.number().int().min(0)).length(5),
});
/**
 * Asking for many reputations at once.
 *
 * A list screen needs a score beside every row, and one request per row would
 * make an applicants page with twenty applicants issue twenty-one requests.
 */
export const reputationBatchInputSchema = z.object({
    personIds: z.array(z.string().uuid()).min(1).max(100),
});
/** Keyed by the same publicId that was asked for. */
export const reputationBatchSchema = z.object({
    reputations: z.record(z.string(), reputationSchema),
});
/** An assignment the signed-in account still owes a review on. */
export const pendingReviewSchema = z.object({
    assignmentId: z.string(),
    jobTitle: z.string(),
    /** The person to review. */
    subjectName: z.string(),
    subjectId: z.string(),
    completedAt: z.string().nullable(),
});
//# sourceMappingURL=reviews.js.map